import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CtaBanner from '../components/CtaBanner';

const CAPABILITIES = [
  { label: 'ADAS', icon: 'i-shield' },
  { label: 'DMS', icon: 'i-eye' },
  { label: 'Live Tracking', icon: 'i-live' },
  { label: 'GPS', icon: 'i-gps' },
  { label: 'Solar Powered', icon: 'i-sun' },
  { label: 'Native Android', icon: 'i-android' },
  { label: 'Native iOS', icon: 'i-mobile' },
  { label: 'AI Vision', icon: 'i-ai' },
];

const SERVICES = [
  { icon: 'i-mobile', title: 'Mobile App Development', body: "100% native Android & iOS apps built around your requirements. We don't build cross-platform apps — ever — because performance always comes first." },
  { icon: 'i-dashcam', title: 'AI Vehicle Safety Dash Cam', body: 'ADAS (advanced driver assistance), DMS (driver monitoring), live monitoring and live tracking — the full hardware and software system controlled by us.' },
  { icon: 'i-video', title: 'Front & Rear Dash Cam Recording', body: 'Simultaneous front (2K/4K) and rear video recording, so both directions are covered on every drive.' },
  { icon: 'i-gps', title: 'Vehicle GPS Tracking', body: 'Real-time GPS tracking for individual vehicles and fleets, so you always know where they are.' },
  { icon: 'i-cctv', title: 'AI Solar CCTV Cameras', body: 'Solar-powered, AI-enabled surveillance — no mains power needed, watching your property around the clock.' },
];

const PROCESS = [
  { title: 'Discover', body: 'Understanding your goals, users and constraints.' },
  { title: 'Design', body: 'Wireframes and UI design validated with your team.' },
  { title: 'Build', body: 'Native app and/or hardware development with regular demos.' },
  { title: 'Deploy', body: 'Launched to app stores or installed in the field, fully tested.' },
  { title: 'Support', body: 'Ongoing maintenance and monitoring after launch.' },
];

const FAQS = [
  { q: 'Do you build cross-platform apps?', a: 'No. We only build fully native Android and iOS apps. We’ve made a deliberate choice not to use cross-platform frameworks, because native is the only way to guarantee the best possible app performance.' },
  { q: 'Who controls the dash cam, GPS and CCTV systems?', a: 'We do, end-to-end. The hardware, firmware, cloud backend and mobile app for our AI dash cam, GPS tracking and solar CCTV products are all designed and controlled by our own team — not a third-party platform.' },
  { q: 'Do you offer ongoing support and maintenance?', a: 'Yes. We offer post-launch support and maintenance plans to keep your app or hardware system running smoothly.' },
  { q: 'Can you work alongside our existing team?', a: 'Absolutely. We regularly plug into existing product and engineering teams, working directly with your stakeholders rather than through a ticket queue.' },
];

export default function Services() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="kh">
      <Header active="services" />

      <section className="kh-page-banner">
        <div className="kh-glow" style={{ width: 420, height: 420, top: -180, left: -100 }} />
        <div className="kh-glow kh-glow-b" style={{ width: 360, height: 360, top: -140, right: -120 }} />
        <div className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', position: 'relative', textAlign: 'center' }}>
          <div className="kh-fade-1 kh-breadcrumb">
            <a href="/">Home</a><span>/</span><span style={{ color: 'var(--text)' }}>Services</span>
          </div>
          <h1 className="kh-fade-2 kh-page-title">Our Services</h1>
          <p className="kh-fade-3 kh-page-sub" style={{ maxWidth: 600 }}>Native mobile apps and AI-powered safety hardware &mdash; dash cams, GPS trackers and solar CCTV &mdash; designed, built and controlled entirely by our own team.</p>
        </div>
      </section>

      <section className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '90px 24px 40px' }}>
        <div className="kh-services-grid kh-services-grid-3">
          {SERVICES.map((s) => (
            <div className="kh-card" key={s.title}>
              <div className="kh-icon-badge"><svg width="24" height="24"><use href={`#${s.icon}`} /></svg></div>
              <h3 className="kh-card-title">{s.title}</h3>
              <p className="kh-card-body">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 24px 90px' }}>
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 32px' }}>
          <div className="kh-eyebrow"><span className="kh-dot" />RESEARCH &amp; DEVELOPMENT</div>
          <h2 style={{ fontSize: 26, fontWeight: 700, marginTop: 16, color: 'var(--text)' }}>What we're building next</h2>
          <p style={{ fontSize: 14.5, color: 'var(--muted)', marginTop: 10, lineHeight: 1.7 }}>Upcoming ideas currently in research and development &mdash; not yet available.</p>
        </div>
        <div className="kh-upcoming-grid">
          <div className="kh-upcoming-card">
            <span className="kh-upcoming-badge">Upcoming</span>
            <div className="kh-icon-badge" style={{ width: 56, height: 56, borderRadius: 16 }}><svg width="24" height="24"><use href="#i-home" /></svg></div>
            <h3 className="kh-card-title" style={{ fontSize: 18, marginTop: 18 }}>Smart Home System</h3>
            <p className="kh-card-body">Control your entire home from your phone &mdash; IoT devices and sensors automate lighting, security and more.</p>
          </div>
          <div className="kh-upcoming-card">
            <span className="kh-upcoming-badge">Upcoming</span>
            <div className="kh-icon-badge" style={{ width: 56, height: 56, borderRadius: 16 }}><svg width="24" height="24"><use href="#i-leaf" /></svg></div>
            <h3 className="kh-card-title" style={{ fontSize: 18, marginTop: 18 }}>Smart Agriculture</h3>
            <p className="kh-card-body">Automated water scheduling with solar-powered CCTV monitoring, plus animal alerts for elephants, cattle, goats and intruders.</p>
          </div>
        </div>
      </section>

      <section className="kh-alt-section">
        <div className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 60px' }}>
            <div className="kh-eyebrow"><span className="kh-dot" />OUR PROCESS</div>
            <h2 className="kh-h2" style={{ marginTop: 18 }}>How we work</h2>
            <p className="kh-lede">A clear, five-stage process from first conversation to ongoing support.</p>
          </div>
          <div className="kh-process-grid">
            {PROCESS.map((step, i) => (
              <div className="kh-process-step" key={step.title}>
                <div className="kh-process-num">{i + 1}</div>
                <h3 className="kh-process-title">{step.title}</h3>
                <p className="kh-process-body">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="kh-tech-strip" style={{ borderTop: 'none' }}>
        <p className="kh-strip-label">BUILT-IN CAPABILITIES</p>
        <div className="kh-marquee-mask">
          <div className="kh-marquee-track">
            {CAPABILITIES.concat(CAPABILITIES).map((cap, i) => (
              <div className="kh-cap-card" key={i}>
                <div className="kh-cap-icon"><svg width="22" height="22"><use href={`#${cap.icon}`} /></svg></div>
                <span className="kh-cap-label">{cap.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '90px 24px' }}>
        <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 48px' }}>
          <div className="kh-eyebrow"><span className="kh-dot" />FAQ</div>
          <h2 className="kh-h2" style={{ marginTop: 18 }}>Common questions</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760, margin: '0 auto' }}>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <div className="kh-faq-item" key={item.q}>
                <button
                  className="kh-faq-q"
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                  aria-expanded={isOpen}
                >
                  <span>{item.q}</span>
                  <span className="kh-faq-icon" style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}>
                    <svg width="13" height="13"><use href="#i-plus" /></svg>
                  </span>
                </button>
                {isOpen && (
                  <div style={{ padding: '0 22px 20px' }}>
                    <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--muted)' }}>{item.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <CtaBanner />
      <Footer />
    </div>
  );
}
